import React, { useMemo, useRef, useState } from 'react';
import { bulkGiftCoins } from '../api/skipzyAdmin';

// Bulk coin gift from a CSV, sent through the app's /admin/notify. A phone with
// no Skipzy account is not notified and no user is created for it.
const DEFAULT_EXPIRY_DAYS = 30;
const DEFAULT_MESSAGE = 'Thank you for joining us! We have added coins to your Skipzy wallet.';
const CHUNK = 200;
const PHONE_COL = 'whatsapp_number';
const COINS_COL = 'total_points';

const BTN =
  'inline-flex items-center justify-center gap-2 rounded-md px-4 py-2 text-sm font-semibold ' +
  'transition-[transform,background-color,opacity,filter] duration-150 ease-[cubic-bezier(0.23,1,0.32,1)] ' +
  'active:scale-[0.97] disabled:opacity-50 disabled:active:scale-100 disabled:cursor-not-allowed ' +
  'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-offset-2 whitespace-nowrap';
const BTN_GREEN = `${BTN} bg-zoddle-green text-gray-900 hover:brightness-95 focus-visible:ring-zoddle-green/60`;
const BTN_GHOST = `${BTN} border border-gray-200 bg-white text-gray-600 hover:bg-gray-50 focus-visible:ring-gray-400/60`;
const FIELD =
  'rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-800 transition-colors duration-150 ' +
  'focus:border-gray-400 focus:outline-none focus:ring-2 focus:ring-zoddle-green/40';

const CSS = `
@keyframes cb-enter { from { opacity: 0; transform: translateY(6px); } to { opacity: 1; transform: none; } }
.cb-enter { animation: cb-enter 220ms cubic-bezier(0.23, 1, 0.32, 1) both; }
@keyframes cb-coin { 0% { transform: translateY(0) rotateY(0); } 50% { transform: translateY(-4px) rotateY(180deg); } 100% { transform: translateY(0) rotateY(360deg); } }
.cb-coin { animation: cb-coin 1.6s ease-in-out infinite; }
@media (prefers-reduced-motion: reduce) { .cb-enter, .cb-coin { animation: none; } }`;

// Quote-aware CSV parser (the pre-entry parser in Inbound.jsx is not).
function parseCsv(text) {
  const rows = [];
  let row = [];
  let cell = '';
  let quoted = false;
  const src = text.replace(/^﻿/, '');
  for (let i = 0; i < src.length; i++) {
    const c = src[i];
    if (quoted) {
      if (c === '"' && src[i + 1] === '"') { cell += '"'; i++; }
      else if (c === '"') quoted = false;
      else cell += c;
    } else if (c === '"') quoted = true;
    else if (c === ',') { row.push(cell); cell = ''; }
    else if (c === '\n' || c === '\r') {
      if (c === '\r' && src[i + 1] === '\n') i++;
      row.push(cell); cell = '';
      if (row.some(v => v.trim() !== '')) rows.push(row);
      row = [];
    } else cell += c;
  }
  row.push(cell);
  if (row.some(v => v.trim() !== '')) rows.push(row);
  if (!rows.length) return { headers: [], records: [] };
  const headers = rows[0].map(h => h.trim());
  const records = rows.slice(1).map(r => Object.fromEntries(headers.map((h, i) => [h, (r[i] ?? '').trim()])));
  return { headers, records };
}

function normalizePhone(raw) {
  let d = String(raw || '').replace(/\D/g, '');
  if (d.length === 12 && d.startsWith('91')) d = d.slice(2);
  if (d.length === 11 && d.startsWith('0')) d = d.slice(1);
  return /^[6-9]\d{9}$/.test(d) ? d : null;
}

function validate(records, phoneCol, coinsCol) {
  const seen = new Set();
  return records.map((rec, i) => {
    const phone = normalizePhone(rec[phoneCol]);
    const coinsRaw = String(rec[coinsCol] ?? '').replace(/,/g, '');
    const coins = /^\d+$/.test(coinsRaw) ? parseInt(coinsRaw, 10) : NaN;
    let error = null;
    if (!phone) error = 'Bad phone';
    else if (!Number.isFinite(coins) || coins <= 0) error = 'Bad coins';
    else if (seen.has(phone)) error = 'Duplicate phone';
    if (phone && !error) seen.add(phone);
    return { id: i, rec, phone, coins: Number.isFinite(coins) ? coins : null, error };
  });
}

const RESULT_PILL = {
  credited: ['bg-green-50 text-green-800', 'Credited'],
  no_account: ['bg-amber-50 text-amber-800', 'No app account'],
  failed: ['bg-red-50 text-red-800', 'Failed'],
};

function Pill({ tone, children }) {
  return <span className={`rounded-full px-2 py-0.5 text-xs font-semibold ${tone}`}>{children}</span>;
}

function Stat({ label, value, tone = 'text-gray-900' }) {
  return (
    <div className="rounded-xl border border-gray-200 bg-white px-4 py-3">
      <div className={`text-2xl font-semibold tabular-nums tracking-tight ${tone}`}>{value}</div>
      <div className="text-xs font-medium uppercase tracking-wide text-gray-500">{label}</div>
    </div>
  );
}

export default function CoinsBulkSection() {
  const inputRef = useRef(null);
  const [fileName, setFileName] = useState('');
  const [parsed, setParsed] = useState(null);
  const [expiry, setExpiry] = useState(String(DEFAULT_EXPIRY_DAYS));
  const [message, setMessage] = useState(DEFAULT_MESSAGE);
  const [dragging, setDragging] = useState(false);
  const [armed, setArmed] = useState(false);
  const [sending, setSending] = useState(false);
  const [results, setResults] = useState({});
  const [done, setDone] = useState(false);
  const [error, setError] = useState('');

  const rows = useMemo(
    () => (parsed ? validate(parsed.records, PHONE_COL, COINS_COL) : []),
    [parsed],
  );
  const valid = rows.filter(r => !r.error);
  const invalid = rows.length - valid.length;
  const totalCoins = valid.reduce((s, r) => s + r.coins, 0);
  const expiryOk = /^\d+$/.test(expiry) && parseInt(expiry, 10) > 0;
  const sentCount = Object.keys(results).length;

  function reset() {
    setFileName(''); setParsed(null); setResults({}); setDone(false);
    setArmed(false); setError('');
    if (inputRef.current) inputRef.current.value = '';
  }

  function loadFile(file) {
    if (!file) return;
    if (!/\.csv$/i.test(file.name)) { setError('Please upload a .csv file.'); return; }
    const reader = new FileReader();
    reader.onload = ev => {
      const p = parseCsv(String(ev.target.result));
      if (!p.headers.length || !p.records.length) { setError('That file has no data rows.'); return; }
      const missing = [PHONE_COL, COINS_COL].filter(c => !p.headers.includes(c));
      if (missing.length) { setError(`Missing column${missing.length > 1 ? 's' : ''}: ${missing.join(', ')}`); return; }
      setError(''); setResults({}); setDone(false); setArmed(false);
      setFileName(file.name);
      setParsed(p);
    };
    reader.readAsText(file);
  }

  async function send() {
    setArmed(false); setSending(true); setResults({}); setError('');
    for (let i = 0; i < valid.length; i += CHUNK) {
      const chunk = valid.slice(i, i + CHUNK);
      let out = {};
      try {
        const data = await bulkGiftCoins({
          rows: chunk.map(r => ({ phone: r.phone, coins: r.coins })),
          expiry_days: parseInt(expiry, 10),
          message: message.trim(),
        });
        const byPhone = Object.fromEntries(data.results.map(x => [x.phone, x]));
        chunk.forEach(r => {
          const x = byPhone[r.phone];
          out[r.id] = x?.notified ? { result: 'credited' }
            : { result: x?.reason?.startsWith('Request failed') ? 'failed' : 'no_account', message: x?.reason };
        });
      } catch (err) {
        const detail = err.response?.data?.detail;
        const msg = typeof detail === 'string' ? detail : err.message;
        chunk.forEach(r => { out[r.id] = { result: 'failed', message: msg }; });
      }
      setResults(prev => ({ ...prev, ...out }));
    }
    setSending(false); setDone(true);
  }

  function downloadResults() {
    const esc = v => `"${String(v ?? '').replace(/"/g, '""')}"`;
    const lines = ['phone,coins,expiry_days,result,message'];
    rows.forEach(r => {
      const res = results[r.id];
      const outcome = r.error ? `skipped: ${r.error}` : res?.result || 'not sent';
      lines.push([r.phone || r.rec[PHONE_COL], r.coins ?? '', expiry, outcome, res?.message || ''].map(esc).join(','));
    });
    const blob = new Blob([lines.join('\n')], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = `coins-results-${fileName || 'upload'}`; a.click();
    URL.revokeObjectURL(url);
  }

  function downloadTemplate() {
    const blob = new Blob(['whatsapp_number,total_points\n9876543210,150\n9123456780,90\n'], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url; a.download = 'coins-template.csv'; a.click();
    URL.revokeObjectURL(url);
  }

  const noAccount = Object.values(results).filter(r => r.result === 'no_account').length;
  const failed = Object.values(results).filter(r => r.result === 'failed').length;
  const pct = valid.length ? Math.round((sentCount / valid.length) * 100) : 0;

  return (
    <div className="space-y-5">
      <style>{CSS}</style>

      <div className="flex flex-wrap items-end justify-between gap-3">
        <div>
          <h3 className="text-lg font-semibold tracking-tight text-gray-900">Bulk gift coins</h3>
          <p className="text-sm text-gray-500">
            Upload a CSV. Coins are added to each phone's Skipzy wallet and the customer gets a push. Phones without the app are skipped.
          </p>
        </div>
        <button type="button" onClick={downloadTemplate} className="text-sm font-medium text-gray-600 underline underline-offset-2 hover:text-gray-900">
          Download template CSV
        </button>
      </div>

      {!parsed && (
        <div
          onDragOver={e => { e.preventDefault(); setDragging(true); }}
          onDragLeave={() => setDragging(false)}
          onDrop={e => { e.preventDefault(); setDragging(false); loadFile(e.dataTransfer.files[0]); }}
          onClick={() => inputRef.current?.click()}
          role="button"
          tabIndex={0}
          onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') inputRef.current?.click(); }}
          className={`cb-enter flex cursor-pointer flex-col items-center justify-center gap-3 rounded-2xl border-2 border-dashed px-6 py-14 text-center transition-colors duration-150 ${
            dragging ? 'border-zoddle-green bg-green-50' : 'border-gray-300 bg-white hover:border-gray-400 hover:bg-gray-50'
          }`}
        >
          <div className="cb-coin flex h-14 w-14 items-center justify-center rounded-full bg-amber-300 text-2xl font-bold text-amber-900 shadow-inner">₹</div>
          <div>
            <p className="font-semibold text-gray-900">Drop your CSV here</p>
            <p className="text-sm text-gray-500">or click to browse. Needs columns whatsapp_number and total_points (coins = total_points).</p>
          </div>
          <input ref={inputRef} type="file" accept=".csv,text/csv" className="hidden" onChange={e => loadFile(e.target.files[0])} />
        </div>
      )}

      {error && <div className="rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-800">{error}</div>}

      {parsed && (
        <div className="cb-enter space-y-5">
          <div className="flex flex-wrap items-end gap-4 rounded-xl border border-gray-200 bg-white p-4">
            <div className="min-w-0">
              <div className="text-xs font-medium uppercase tracking-wide text-gray-500">File</div>
              <div className="max-w-[16rem] truncate text-sm font-semibold text-gray-900">{fileName}</div>
            </div>
            <label className="text-sm">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Expires in (days)</span>
              <input
                value={expiry}
                onChange={e => setExpiry(e.target.value.replace(/\D/g, ''))}
                disabled={sending || done}
                inputMode="numeric"
                className={`${FIELD} w-28 tabular-nums`}
              />
            </label>
            <label className="min-w-[16rem] flex-1 text-sm">
              <span className="mb-1 block text-xs font-medium uppercase tracking-wide text-gray-500">Push message</span>
              <input
                value={message}
                onChange={e => setMessage(e.target.value)}
                disabled={sending || done}
                maxLength={400}
                className={`${FIELD} w-full`}
              />
            </label>
            <button type="button" onClick={reset} disabled={sending} className={`${BTN_GHOST} ml-auto`}>Choose another file</button>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
            <Stat label="Rows" value={rows.length} />
            <Stat label="Ready" value={valid.length} tone="text-green-700" />
            <Stat label="Need fixing" value={invalid} tone={invalid ? 'text-[#B93573]' : 'text-gray-900'} />
            <Stat label="Total coins" value={totalCoins.toLocaleString('en-IN')} />
          </div>

          {(sending || done) && (
            <div className="cb-enter rounded-xl border border-gray-200 bg-white p-4">
              <div className="mb-2 flex items-center justify-between text-sm">
                <span className="font-semibold text-gray-900">
                  {done ? 'Finished' : 'Sending…'} {sentCount}/{valid.length}
                </span>
                {done && (
                  <span className="text-gray-500">
                    {sentCount - noAccount - failed} credited · {noAccount} no app account · {failed} failed
                  </span>
                )}
              </div>
              <div className="h-2 overflow-hidden rounded-full bg-gray-100">
                <div className="h-full rounded-full bg-zoddle-green transition-[width] duration-200 ease-out" style={{ width: `${pct}%` }} />
              </div>
            </div>
          )}

          <div className="max-h-[28rem] overflow-auto rounded-xl border border-gray-200 bg-white">
            <table className="w-full text-sm">
              <thead className="sticky top-0 bg-gray-50 text-left text-xs uppercase tracking-wide text-gray-500">
                <tr>
                  <th className="px-4 py-2">#</th>
                  <th className="px-4 py-2">Phone</th>
                  <th className="px-4 py-2 text-right">Coins</th>
                  <th className="px-4 py-2">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100">
                {rows.map(r => {
                  const res = results[r.id];
                  return (
                    <tr key={r.id} className={r.error ? 'bg-red-50/40' : ''}>
                      <td className="px-4 py-2 tabular-nums text-gray-400">{r.id + 1}</td>
                      <td className="px-4 py-2 tabular-nums text-gray-800">{r.phone || r.rec[PHONE_COL] || '—'}</td>
                      <td className="px-4 py-2 text-right tabular-nums font-medium text-gray-900">{r.coins ?? '—'}</td>
                      <td className="px-4 py-2">
                        {r.error ? <Pill tone="bg-red-50 text-red-800">{r.error}</Pill>
                          : res ? <Pill tone={RESULT_PILL[res.result][0]}>{RESULT_PILL[res.result][1]}</Pill>
                          : <Pill tone="bg-gray-100 text-gray-600">{sending ? 'Waiting…' : 'Valid'}</Pill>}
                        {res?.message && <span className="ml-2 text-xs text-red-700">{res.message}</span>}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>

          <div className="flex flex-wrap items-center justify-between gap-3">
            <p className="text-sm text-gray-500">
              {invalid > 0 && !done ? `${invalid} row${invalid === 1 ? '' : 's'} will be skipped.` : ''}
            </p>
            <div className="flex items-center gap-2">
              {done && <button type="button" onClick={downloadResults} className={BTN_GHOST}>Download results CSV</button>}
              {!done && !armed && (
                <button
                  type="button"
                  onClick={() => setArmed(true)}
                  disabled={sending || !valid.length || !expiryOk || !message.trim()}
                  className={BTN_GREEN}
                >
                  Gift {totalCoins.toLocaleString('en-IN')} coins to {valid.length} {valid.length === 1 ? 'person' : 'people'}
                </button>
              )}
              {!done && armed && (
                <>
                  <button type="button" onClick={send} className={BTN_GREEN}>
                    Confirm: expires in {expiry} days
                  </button>
                  <button type="button" onClick={() => setArmed(false)} className={BTN_GHOST}>Cancel</button>
                </>
              )}
              {done && <button type="button" onClick={reset} className={BTN_GREEN}>Upload another</button>}
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
